import type { AnchorHTMLAttributes, FC } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { colors, fonts } from "../design-tokens";

// Common typography base styles
const baseTypography = css`
  ${fonts.bodyFont}
  font-style: normal;
  text-wrap: pretty;
`;

// Common heading typography base styles
const baseHeadingTypography = css`
  ${fonts.headingFont}
  font-style: normal;
  text-wrap: balance;
`;

// Base body text with the specified typography
export const BodyRegular = styled.p`
  ${baseTypography}
  color: ${colors.text};
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
`;

// For inline text within paragraphs
export const BodyRegularSpan = styled.span`
  ${baseTypography}
  color: ${colors.text};
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
`;

// Styled link with the same base typography
export const BodyLink = styled.a`
  ${baseTypography}
  color: ${colors.text};
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;

  &:hover {
    text-decoration-thickness: 2px;
  }

  &:focus {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }
`;

export const NewTabBodyLink: FC<AnchorHTMLAttributes<HTMLAnchorElement>> = ({
  children,
  href,
  ...props
}) => {
  // For tel: and mailto: links, don't open in new tab
  const isExternalProtocol =
    href && (href.startsWith("tel:") || href.startsWith("mailto:"));

  if (isExternalProtocol) {
    return (
      <BodyLink href={href} {...props}>
        {children}
      </BodyLink>
    );
  }

  // For regular http/https links, open in new tab to preserve chat context
  return (
    <BodyLink href={href} {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </BodyLink>
  );
};

// Styled list with proper typography
export const BodyList = styled.ul`
  ${baseTypography}
  color: ${colors.text};
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  margin: 16px 0;
  padding-left: 24px;
`;

export const OrderedBodyList = styled.ol`
  ${baseTypography}
  color: ${colors.text};
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
`;

// Styled list item
export const BodyListItem = styled.li`
  ${baseTypography}
  color: ${colors.text};
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  margin-bottom: 8px;
`;

// Headers if needed
export const Heading1 = styled.h1`
  ${baseHeadingTypography}
  color: ${colors.text};
  font-size: 28px;
  font-weight: 600;
  line-height: normal;
  margin: 0 0 16px 0;
`;

export const Heading2 = styled.h2`
  ${baseHeadingTypography}
  color: ${colors.text};
  font-size: 24px;
  font-weight: 600;
  line-height: normal;
  margin: 0 0 12px 0;
`;

export const Heading3 = styled.h3`
  ${baseHeadingTypography}
  color: ${colors.text};
  font-size: 20px;
  font-weight: 600;
  line-height: normal;
  margin: 0 0 8px 0;
`;

// H4 from typographies.tsx
export const Heading4 = styled.h4`
  ${baseHeadingTypography}
  color: ${colors.text};
  font-size: 36px;
  font-weight: 300;
  line-height: 120%;
  letter-spacing: -0.54px;
  margin: 0;
`;

// H5 for section titles like Sources, Related Questions, etc.
export const Heading5 = styled.h5`
  ${baseHeadingTypography}
  color: ${colors.text};
  font-size: 26px;
  font-weight: 300;
  line-height: normal;
  margin: 0;
`;

// Body text with alignment option from typographies.tsx
export const BodyText = styled.p<{ textAlign?: "center" | "left" | "right" }>`
  ${baseTypography}
  color: ${colors.textSecondary};
  text-align: ${(props) => props.textAlign ?? "left"};
  font-size: 18px;
  font-weight: 400;
  line-height: normal;
  margin: 0;
`;

// Question text from typographies.tsx
export const QuestionText = styled.p`
  ${baseTypography}
  color: ${colors.text};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
  margin: 0;
`;

// Body Small - for next best actions and similar smaller text
export const BodySmall = styled.span`
  ${baseTypography}
  color: ${colors.text};
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
`;
